import React, { useState, useEffect } from 'react';
import { db } from '../services/db';
import { XCircle, ShieldCheck, Loader2, AlertCircle } from 'lucide-react';

export const VerifyReportPage = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [timestamp, setTimestamp] = useState<string>('');

  useEffect(() => {
    const p2 = (n: number) => n < 10 ? '0' + n : n;
    const tick = () => {
      const d = new Date();
      const mo = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      setTimestamp(`${p2(d.getDate())} ${mo[d.getMonth()]} ${d.getFullYear()}, ${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}`);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const dataParam = urlParams.get('data');
    const idParam = urlParams.get('id');
    const hashParam = urlParams.get('h');
    const termParam = urlParams.get('t');
    
    const processData = async () => {
      try {
        if (idParam && hashParam) {
          // New format: /result?id=ID&h=HASH
          const salt = "SSF_SECURE_2026";
          let hash = 0;
          const str = idParam + salt;
          for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash;
          }
          const expectedHash = Math.abs(hash).toString(36);
          
          if (hashParam !== expectedHash) {
            setError("Security verification failed. This link may have been tampered with.");
            setLoading(false);
            return;
          }
          
          console.log("Verifying with id:", idParam, "and hash:", hashParam);
          const res = await db.checkResultById(idParam);
          console.log("checkResult result:", res);
          
          setData({
            student: res.student,
            school: res.school,
            term: termParam || 'Term 1',
            academicYears: res.academicYears || [],
            studentRecords: res.studentRecords || [],
            urlAy: urlParams.get('ay') || null
          });
        } else if (dataParam) {
          // Old format: ?view=verify&data=...
          const decoded = JSON.parse(decodeURIComponent(escape(atob(dataParam))));
          if (decoded.v === 2) {
            const res = await db.checkResult(decoded.sd, decoded.i);
            if (res) {
              setData({
                student: res.student,
                school: res.school,
                term: decoded.t,
                academicYears: res.academicYears || [],
                studentRecords: res.studentRecords || [],
                urlAy: urlParams.get('ay') || null
              });
            } else {
              setError("Report Card not found or invalid.");
            }
          } else {
            setData({
              student: {
                full_name: decoded.s.n,
                index_number: decoded.s.i,
                class_grade: decoded.s.c,
                stream: decoded.s.st,
                gender: decoded.s.g,
                dob: decoded.s.d
              },
              school: {
                name: decoded.sc.n,
                district: decoded.sc.d,
                sector: decoded.sc.s,
                cell: decoded.sc.c,
                phone: decoded.sc.p,
                email: decoded.sc.e,
                level: decoded.sc.l,
                sdms_code: decoded.sc.sd
              },
              term: decoded.t,
              urlAy: urlParams.get('ay') || decoded.ay || null
            });
          }
        } else {
          setError("No verification data provided.");
        }
      } catch (e) {
        console.error("Verification error:", e);
        setError(e instanceof Error ? e.message : "Invalid verification link.");
      } finally {
        setLoading(false);
      }
    };
    processData();
  }, []);

  const doShare = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: `${data?.student?.full_name || 'Student'} – Report Card`, url }).catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => alert('Link copied!')).catch(() => prompt('Copy:', url));
    } else {
      prompt('Copy this link:', url);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex flex-col items-center justify-center p-6 font-['Plus_Jakarta_Sans',sans-serif]">
        <div className="bg-white p-10 rounded-[32px] shadow-xl shadow-blue-900/5 border border-slate-200 text-center max-w-md w-full animate-in fade-in zoom-in duration-300">
          <div className="w-20 h-20 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <Loader2 size={48} className="animate-spin" strokeWidth={1.5} />
          </div>
          <h2 className="text-2xl font-extrabold text-slate-900 mb-3">Verifying Report</h2>
          <p className="text-slate-500 font-medium leading-relaxed mb-8">
            Please wait while we securely verify the authenticity of this academic document...
          </p>
          <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
            <div className="bg-blue-600 h-full w-1/2 animate-[loading_1.5s_infinite_ease-in-out]"></div>
          </div>
          <style>{`
            @keyframes loading {
              0% { transform: translateX(-100%); }
              100% { transform: translateX(200%); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  if (error || !data || !data.student || !data.school) {
    const displayError = error || (!data ? "We couldn't verify the authenticity of this report card." : "The report card content appears to be incomplete or missing.");
    return (
      <div className="min-h-screen bg-[#f8fafc] flex flex-col items-center justify-center p-6 font-['Plus_Jakarta_Sans',sans-serif]">
        <div className="bg-white p-10 rounded-[32px] shadow-xl shadow-blue-900/5 border border-slate-200 text-center max-w-md w-full animate-in fade-in zoom-in duration-300">
          <div className="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle size={48} strokeWidth={1.5} />
          </div>
          <h2 className="text-2xl font-extrabold text-slate-900 mb-3">Verification Failed</h2>
          <div className="bg-red-50/50 border border-red-100 rounded-2xl p-4 mb-8">
            <p className="text-red-700 font-semibold text-sm leading-relaxed">
              {displayError}
            </p>
          </div>
          
          <div className="space-y-3">
            <button 
              onClick={() => window.location.reload()} 
              className="w-full py-4 bg-slate-900 text-white rounded-2xl font-bold text-sm hover:bg-slate-800 transition-all active:scale-[0.98] shadow-lg shadow-slate-200"
            >
              Try Again
            </button>
            <button 
              onClick={() => window.location.href = '/'} 
              className="w-full py-4 bg-white text-slate-600 border border-slate-200 rounded-2xl font-bold text-sm hover:bg-slate-50 transition-all active:scale-[0.98]"
            >
              Return to Home
            </button>
          </div>
          
          <p className="mt-8 text-[11px] text-slate-400 font-medium uppercase tracking-widest">
            Smart School Flow Security System
          </p>
        </div>
      </div>
    );
  }

  const { student, school, term } = data;
  const currentYear = new Date().getFullYear();
  const studentIdStr = String(student.index_number || student.id || '00000');
  const reportId = `RPT-${currentYear}-${studentIdStr.slice(-5)}`;

  // Dynamically resolve matching Academic Year
  const resolvedAcademicYear = (() => {
    if (data.urlAy) return data.urlAy;
    if (data.academicYears && data.academicYears.length > 0) {
      if (student?.academic_year_id) {
        const ay = data.academicYears.find((y: any) => y.id === student.academic_year_id);
        if (ay) return ay.year_name;
      }
      if (data.studentRecords && data.studentRecords.length > 0) {
        const firstRecord = data.studentRecords[0];
        if (firstRecord?.academic_year_id) {
          const ay = data.academicYears.find((y: any) => y.id === firstRecord.academic_year_id);
          if (ay) return ay.year_name;
        }
      }
      const activeAy = data.academicYears.find((y: any) => y.is_current || y.status === 'active');
      if (activeAy) return activeAy.year_name;
    }
    return `${currentYear} – ${currentYear + 1}`;
  })();

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-800 font-['Inter',sans-serif] antialiased flex flex-col items-center py-8 px-4 sm:px-6">
      <div className="w-full max-w-[480px] bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200/60 animate-in slide-in-from-bottom-8 duration-700 ease-out">
        
        {/* HEADER */}
        <div className="bg-[#1e3a8a] py-6 px-4 text-center">
          <div className="flex justify-center mb-3">
            <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-xl relative">
              <div className="w-4 h-4 bg-white rotate-45 transform"></div>
              <div className="w-4 h-4 bg-white/50 rotate-45 transform absolute top-2"></div>
            </div>
          </div>
          <h1 className="text-xl font-bold text-white mb-1.5">{school.name}</h1>
          <p className="text-[#93c5fd] text-xs font-medium tracking-wide">Official Document Verification Portal</p>
        </div>

        <div className="p-6 sm:p-8">
          {/* VERIFICATION STATUS */}
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-[#dcfce7] rounded-full flex items-center justify-center mb-4 shadow-[0_0_0_8px_rgba(220,252,231,0.4)]">
              <ShieldCheck className="w-8 h-8 text-[#16a34a]" />
            </div>
            <h2 className="text-2xl font-bold text-[#16a34a] mb-2">Document Verified</h2>
            <p className="text-slate-500 text-sm leading-relaxed max-w-sm mx-auto">
              This report card is authentic and officially issued by <br className="hidden sm:block" />
              <span className="font-semibold text-slate-700">{school.name}</span>
            </p>
          </div>

          <hr className="border-slate-100 mb-8" />

          {/* STUDENT INFORMATION */}
          <div className="mb-8">
            <h3 className="text-[11px] font-bold tracking-[0.15em] text-slate-400 uppercase mb-4">Student Information</h3>
            <div className="bg-[#f8fafc] rounded-xl p-5 border border-slate-100 space-y-3.5">
              <div className="flex justify-between items-start gap-4">
                <span className="text-slate-500 text-sm">Student Name</span>
                <span className="text-slate-900 font-semibold text-sm text-right">{student.full_name}</span>
              </div>
              <div className="flex justify-between items-start gap-4">
                <span className="text-slate-500 text-sm">Student ID</span>
                <span className="text-slate-900 font-medium text-sm font-mono text-right">{student.index_number || (student.id ? String(student.id).slice(0,8).toUpperCase() : 'N/A')}</span>
              </div>
              <div className="flex justify-between items-start gap-4">
                <span className="text-slate-500 text-sm">Grade & Section</span>
                <span className="text-slate-900 font-medium text-sm text-right">{student.class_grade} {student.stream && `— Section ${student.stream}`}</span>
              </div>
            </div>
          </div>

          {/* DOCUMENT DETAILS */}
          <div className="mb-6">
            <h3 className="text-[11px] font-bold tracking-[0.15em] text-slate-400 uppercase mb-4">Document Details</h3>
            <div className="bg-[#f8fafc] rounded-xl p-5 border border-slate-100 space-y-3.5">
              <div className="flex justify-between items-start gap-4">
                <span className="text-slate-500 text-sm">Document Type</span>
                <span className="text-slate-800 font-medium text-sm text-right">Student Report Card</span>
              </div>
              <div className="flex justify-between items-start gap-4">
                <span className="text-slate-500 text-sm">Academic Year</span>
                <span className="text-slate-800 font-medium text-sm text-right">{resolvedAcademicYear}</span>
              </div>
              <div className="flex justify-between items-start gap-4">
                <span className="text-slate-500 text-sm">Term / Period</span>
                <span className="text-slate-800 font-medium text-sm text-right">{term}</span>
              </div>
              <div className="flex justify-between items-start gap-4">
                <span className="text-slate-500 text-sm">Date Issued</span>
                <span className="text-slate-800 font-medium text-sm text-right">{timestamp.split(',')[0]}</span>
              </div>
            </div>
          </div>

          {/* VERIFICATION BADGE */}
          <div className="bg-[#f0fdf4] border border-[#bbf7d0] rounded-xl p-4 flex gap-3">
            <ShieldCheck className="w-5 h-5 text-[#16a34a] shrink-0 mt-0.5" />
            <div>
              <div className="text-xs font-semibold text-[#166534] mb-0.5">
                Verification ID: VRF-{studentIdStr.slice(-4) || '8X2K'}-{Math.random().toString(36).substring(2, 6).toUpperCase()}-4720
              </div>
              <div className="text-[11px] text-[#15803d]">
                Scanned on {timestamp} · Valid document
              </div>
            </div>
          </div>

        </div>

        {/* FOOTER */}
        <div className="bg-slate-50 border-t border-slate-200/60 p-6 text-center">
          <p className="text-slate-500 text-xs mb-1.5">This verification is digitally signed and tamper-proof.</p>
          <p className="text-slate-500 text-xs">
            For enquiries contact <a href="#" className="flex items-center justify-center gap-1 text-slate-800 font-semibold hover:text-blue-600 border-b border-transparent hover:border-blue-600 transition-colors py-0.5 mt-1 mx-auto max-w-fit">registrar@{(school?.name || 'school').toLowerCase().replace(/[^a-z0-9]/g, '')}.edu</a>
          </p>
        </div>
      </div>

      {/* EXTERNAL FOOTER */}
      <div className="mt-8 text-center text-[11px] text-slate-400 max-w-sm">
        <p className="mb-4">
          <button 
            onClick={() => window.location.href = `/?view=result_check&sdms=${school.sdms_code || school.id}&index=${student.index_number || student.id}&term=${term}&ay=${encodeURIComponent(resolvedAcademicYear)}`}
            className="w-full bg-white text-slate-700 py-3 rounded-xl font-semibold shadow-sm border border-slate-200 hover:bg-slate-50 transition-colors"
          >
            View Detailed Grades
          </button>
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          <span>{(school?.name || 'school').toLowerCase().replace(/[^a-z0-9]/g, '')}.edu</span>
          <span>·</span>
          <span>Secure Verification System</span>
          <span>·</span>
          <span>© {currentYear}</span>
        </div>
      </div>
    </div>
  );
};
